import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { ChevronDown, ChevronUp, History, UserPen, FilePlus2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { fmtMDT } from "../../lib/time";

/* Simple "audit-of-the-auditors" trail: every finding that's been created or
   edited, newest first, with who did it and when. Admin-only — surfaced in
   the main Compliance app's Audits tab, not in the Auditor Portal itself. */
export default function FindingActivityLog() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/compliance/findings/activity");
      setItems(data.items || []);
      setLoaded(true);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open && !loaded) load(); }, [open, loaded, load]);

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="finding-activity-log">
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        onClick={() => setOpen((v) => !v)}
        data-testid="finding-activity-log-toggle"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-muted-foreground" />
          <span className="text-[13px] font-semibold">Finding activity (audit-of-the-auditors)</span>
          <span className="text-[11.5px] text-muted-foreground">who added/edited findings, and when</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="border-t border-border divide-y divide-border max-h-80 overflow-y-auto">
          {loading ? (
            <div className="h-20 bg-muted/20 animate-pulse" />
          ) : items.length === 0 ? (
            <div className="py-6 text-center text-[12.5px] text-muted-foreground">No findings have been logged yet.</div>
          ) : items.map((f) => {
            const edited = !!f.updated_at;
            const actorName = edited ? (f.updated_by_name || f.updated_by) : (f.created_by_name || f.created_by);
            const actorRole = edited ? f.updated_by_role : f.created_by_role;
            const ts = edited ? f.updated_at : f.created_at;
            return (
              <div key={f.id} className="px-4 py-2.5 flex items-start gap-2.5" data-testid={`finding-activity-row-${f.id}`}>
                {edited ? <UserPen className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" /> : <FilePlus2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-medium truncate">{f.title}</div>
                  <div className="text-[11.5px] text-muted-foreground">
                    {edited ? "Edited" : "Created"} by <span className="font-medium text-foreground">{actorName || "Unknown"}</span>
                    {actorRole && <span className={cn("ml-1 rounded-full border px-1.5 py-0 text-[10px] font-semibold", actorRole === "Auditor" ? "border-sky-500/40 bg-sky-500/10 text-sky-600" : "border-border bg-muted/40")}>{actorRole}</span>}
                    {" "}in <span className="text-foreground">{f.audit_name}</span>
                  </div>
                </div>
                <div className="text-[11px] text-muted-foreground shrink-0">{ts ? fmtMDT(ts) : "—"}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
